import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';
import { useToast } from '../../components/Toast';
import { colors, radius, shadows } from '../../styles/theme';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';
const getToken = () => localStorage.getItem('access_token');
const authHeaders = () => ({ 'Authorization': `Bearer ${getToken()}` });

const fmtTutar = (n) =>
  (Number(n) || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 });

// Çıkış sebebi kodları → Türkçe label
const CIKIS_SEBEP_LABELS = {
  ISVEREN_FESHI: 'İşveren Feshi',
  ISTIFA: 'İstifa',
  DENEME_FESHI: 'Deneme Feshi',
  EMEKLILIK: 'Emeklilik',
  ASKERLIK: 'Askerlik',
  EVLILIK: 'Evlilik',
  VEFAT: 'Vefat',
  HAKLI_NEDEN: 'Haklı Neden',
};

const th = {
  padding: '8px 10px',
  textAlign: 'left',
  fontSize: 10,
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
  fontWeight: 700,
  color: '#555',
  borderBottom: `2px solid ${colors.border}`,
  background: '#fafafa',
};
const thR = { ...th, textAlign: 'right' };
const td = { padding: '8px 10px', fontSize: 12 };
const tdR = { ...td, textAlign: 'right' };

const KidemKayitlariPage = () => {
  const { user } = useAuth();
  const toast = useToast();
  const canManage =
    user && hasPermission(user.role, PERMISSIONS.MANAGE_HR, user.effectivePermissions);

  const [kayitlar, setKayitlar] = useState([]);
  const [loading, setLoading] = useState(false);
  const [odemeDurumu, setOdemeDurumu] = useState('');   // '' = tümü
  const [yil, setYil] = useState('');                   // '' = tüm yıllar

  const load = useCallback(async () => {
    if (!canManage) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (odemeDurumu) params.set('odeme_durumu', odemeDurumu);
      if (yil) params.set('yil', yil);
      const url = `${API_URL}/api/hr/kidem-kayitlari${params.toString() ? '?' + params.toString() : ''}`;
      const r = await fetch(url, { headers: authHeaders() });
      if (r.ok) {
        setKayitlar(await r.json());
      } else {
        toast.error('Kıdem kayıtları yüklenemedi');
      }
    } catch {
      toast.error('Bağlantı hatası');
    } finally {
      setLoading(false);
    }
  }, [odemeDurumu, yil, canManage, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleOdendiIsaretle = async (kayit) => {
    if (!window.confirm(
      `${kayit.ad_soyad} için ${fmtTutar(kayit.hesaplanan_tutar)} ₺ kıdem tazminatı ÖDENDİ olarak işaretlenecek.\n\nDevam?`
    )) return;

    try {
      const r = await fetch(`${API_URL}/api/hr/kidem-kayitlari/${kayit.id}`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          odeme_durumu: 'ODENDI',
          odeme_tarihi: new Date().toISOString().split('T')[0],
        }),
      });
      if (r.ok) {
        toast.success('Ödeme durumu güncellendi');
        await load();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Güncelleme başarısız');
      }
    } catch {
      toast.error('Bağlantı hatası');
    }
  };

  const handleBekleyeneCek = async (kayit) => {
    if (!window.confirm(
      `${kayit.ad_soyad} için ödeme BEKLIYOR durumuna geri alınacak. Devam?`
    )) return;
    try {
      const r = await fetch(`${API_URL}/api/hr/kidem-kayitlari/${kayit.id}`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ odeme_durumu: 'BEKLIYOR' }),
      });
      if (r.ok) {
        toast.info('Ödeme BEKLIYOR durumuna alındı');
        await load();
      } else {
        toast.error('Güncelleme başarısız');
      }
    } catch {
      toast.error('Bağlantı hatası');
    }
  };

  if (!canManage) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
        Bu sayfaya erişim yetkiniz yok.
      </div>
    );
  }

  const bekleyenToplam = kayitlar
    .filter(k => k.odeme_durumu === 'BEKLIYOR')
    .reduce((s, k) => s + (Number(k.hesaplanan_tutar) || 0), 0);
  const odenenToplam = kayitlar
    .filter(k => k.odeme_durumu === 'ODENDI')
    .reduce((s, k) => s + (Number(k.hesaplanan_tutar) || 0), 0);

  const yilOptions = [];
  const buYil = new Date().getFullYear();
  for (let y = buYil; y >= 2023; y--) yilOptions.push(y);

  return (
    <div>
      {/* Özet kartlar */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 12,
        marginBottom: 14,
      }}>
        <div style={{
          background: colors.surface,
          borderRadius: radius.md,
          padding: 14,
          boxShadow: shadows.sm,
          borderLeft: '4px solid #1565c0',
        }}>
          <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Toplam Kayıt
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#1565c0' }}>{kayitlar.length}</div>
        </div>
        <div style={{
          background: colors.surface,
          borderRadius: radius.md,
          padding: 14,
          boxShadow: shadows.sm,
          borderLeft: '4px solid #f57c00',
        }}>
          <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Bekleyen Ödeme
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#f57c00' }}>
            {fmtTutar(bekleyenToplam)} ₺
          </div>
        </div>
        <div style={{
          background: colors.surface,
          borderRadius: radius.md,
          padding: 14,
          boxShadow: shadows.sm,
          borderLeft: '4px solid #2e7d32',
        }}>
          <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Ödenen Toplam
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#2e7d32' }}>
            {fmtTutar(odenenToplam)} ₺
          </div>
        </div>
      </div>

      {/* Filtreler */}
      <div style={{
        background: colors.surface,
        borderRadius: radius.md,
        padding: 14,
        boxShadow: shadows.sm,
        marginBottom: 12,
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        flexWrap: 'wrap',
      }}>
        <div>
          <label style={{ fontSize: 11, color: '#666', marginRight: 6 }}>Ödeme Durumu:</label>
          <select
            value={odemeDurumu}
            onChange={e => setOdemeDurumu(e.target.value)}
            style={{ padding: '6px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: 12 }}
          >
            <option value="">Tümü</option>
            <option value="BEKLIYOR">Bekliyor</option>
            <option value="ODENDI">Ödendi</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, color: '#666', marginRight: 6 }}>Yıl:</label>
          <select
            value={yil}
            onChange={e => setYil(e.target.value)}
            style={{ padding: '6px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: 12 }}
          >
            <option value="">Tümü</option>
            {yilOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <button
          onClick={load}
          disabled={loading}
          style={{
            padding: '6px 14px',
            background: '#1565c0',
            color: 'white',
            border: 'none',
            borderRadius: radius.sm,
            cursor: loading ? 'wait' : 'pointer',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {loading ? 'Yükleniyor...' : '↻ Yenile'}
        </button>
      </div>

      {/* Tablo */}
      <div style={{
        background: colors.surface,
        borderRadius: radius.md,
        boxShadow: shadows.sm,
        overflow: 'hidden',
      }}>
        {loading && kayitlar.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Yükleniyor...</div>
        ) : kayitlar.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
            Kayıt bulunamadı. Personel çıkışı yapıldığında hak doğarsa buraya otomatik eklenir.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Çıkış Tarihi</th>
                  <th style={th}>Ad Soyad</th>
                  <th style={th}>Sebep</th>
                  <th style={thR}>Çalışma Süresi</th>
                  <th style={thR}>Hak Gün</th>
                  <th style={thR}>Giydirilmiş Brüt</th>
                  <th style={thR}>Hesaplanan Tutar</th>
                  <th style={th}>Durum</th>
                  <th style={th}>Ödeme Tarihi</th>
                  <th style={th}>Aksiyon</th>
                </tr>
              </thead>
              <tbody>
                {kayitlar.map(k => {
                  const yilTam = Math.floor(k.calisma_yili || 0);
                  const ayKalan = Math.round(((k.calisma_yili || 0) - yilTam) * 12);
                  const sureStr = `${yilTam}y ${ayKalan}a`;
                  const bekliyor = k.odeme_durumu === 'BEKLIYOR';
                  return (
                    <tr key={k.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                      <td style={td}>{k.cikis_tarihi || '-'}</td>
                      <td style={{ ...td, fontWeight: 600 }}>{k.ad_soyad || `#${k.personel_id}`}</td>
                      <td style={td}>
                        {CIKIS_SEBEP_LABELS[k.cikis_sebebi] || k.cikis_sebebi}
                        {k.cikis_sebebi_sgk_kodu && (
                          <span style={{ color: '#888', marginLeft: 4 }}>
                            (Kod {k.cikis_sebebi_sgk_kodu})
                          </span>
                        )}
                      </td>
                      <td style={tdR}>{sureStr}</td>
                      <td style={tdR}>{k.hak_edilen_gun || 0}</td>
                      <td style={tdR}>{fmtTutar(k.giydirilmis_brut)} ₺</td>
                      <td style={{ ...tdR, fontWeight: 700, color: '#1565c0' }}>
                        {fmtTutar(k.hesaplanan_tutar)} ₺
                      </td>
                      <td style={td}>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: 10,
                          fontSize: 10,
                          fontWeight: 700,
                          background: bekliyor ? '#fff3e0' : '#e8f5e9',
                          color: bekliyor ? '#e65100' : '#2e7d32',
                          border: `1px solid ${bekliyor ? '#ffb74d' : '#81c784'}`,
                        }}>
                          {bekliyor ? 'BEKLIYOR' : 'ÖDENDİ'}
                        </span>
                      </td>
                      <td style={td}>{k.odeme_tarihi || '-'}</td>
                      <td style={td}>
                        {bekliyor ? (
                          <button
                            onClick={() => handleOdendiIsaretle(k)}
                            style={{
                              padding: '4px 10px',
                              background: '#2e7d32',
                              color: 'white',
                              border: 'none',
                              borderRadius: radius.sm,
                              cursor: 'pointer',
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          >
                            Ödendi İşaretle
                          </button>
                        ) : (
                          <button
                            onClick={() => handleBekleyeneCek(k)}
                            style={{
                              padding: '4px 10px',
                              background: '#eee',
                              color: '#555',
                              border: `1px solid ${colors.border}`,
                              borderRadius: radius.sm,
                              cursor: 'pointer',
                              fontSize: 11,
                            }}
                            title="Ödeme durumunu BEKLIYOR'a geri al"
                          >
                            Geri Al
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default KidemKayitlariPage;
